import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  TemplateRef,
  computed,
  input,
  output,
  signal,
  inject,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { provideIcons } from '@ng-icons/core';
import {
  lucideMapPin,
  lucideHouse,
  lucideBuilding,
  lucideEllipsis,
  lucideTrash,
  lucidePencil,
  lucidePlus,
  lucideRocket,
} from '@ng-icons/lucide';
import { HlmMenuImports } from '@spartan-ng/helm/menu';
import { Address } from '@shared/models/address.model';
import {
  DataTableComponent,
  ColumnDef,
  TableAction,
  TableSort,
} from '@shared/components/data-table/data-table';
import { AddressForm } from '@shared/components/address-form/address-form';
import { AddressService } from '@shared/services/address.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { ConfirmationDialogService } from '@shared/services/confirmation-dialog.service';
import { ToastService } from '@shared/services/toast.service';

export interface AddressAction {
  type: 'edit' | 'delete' | 'create';
  address?: Address;
}

@Component({
  selector: 'app-address-table',
  standalone: true,
  imports: [
    CommonModule,
    DataTableComponent,
    AddressForm,
    ...HlmMenuImports,
  ],
  providers: [
    provideIcons({
      lucideMapPin,
      lucideHouse,
      lucideBuilding,
      lucideEllipsis,
      lucideTrash,
      lucidePencil,
      lucidePlus,
      lucideRocket,
    }),
  ],
  templateUrl: './address-table.html',
  styleUrl: './address-table.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressTable {
  readonly currentOrgStore = inject(CurrentOrganizationStore);
  // Services
  private readonly addressService = inject(AddressService);
  private readonly confirmationDialog = inject(ConfirmationDialogService);
  private readonly toastService = inject(ToastService);

  // Inputs
  addresses = input.required<Address[]>();
  isLoading = input<boolean>(false);
  showSelectionColumn = input<boolean>(false);
  organizationId = input.required<string>();
  serverSorting = input(false);
  currentPage = input<number>(1);
  totalPages = input<number>(0);
  /** Total address count across all pages — powers the "select all X" banner. */
  totalItems = input<number>(0);
  /** True while the parent is fetching every address for "select all". */
  isSelectingAll = input<boolean>(false);

  // Outputs
  actionTriggered = output<AddressAction>();
  selectionChanged = output<string[]>();
  createCampaignRequested = output<Address[]>();
  pageChange = output<number>();
  sortChange = output<TableSort | null>();
  requestSelectAll = output<void>();
  addressCreated = output<Address>();
  addressUpdated = output<Address>();
  dataChanged = output<void>();

  // Dialog state
  isFormOpen = signal(false);
  editingAddress = signal<Address | null>(null);

  /** Reference to the inner DataTable — used to drive select-all and read selected objects. */
  readonly dataTable = viewChild<DataTableComponent<Address>>(DataTableComponent);

  /** Identity function for ID-based selection in DataTable. */
  readonly getRowId = (addr: Address) => addr.addressId;

  /**
   * Full Address objects for every currently selected ID.
   * Delegates entirely to DataTable's built-in selectedObjects — no local cache needed.
   */
  readonly selectedAddressObjects = computed<Address[]>(
    () => this.dataTable()?.selectedObjects() ?? []
  );

  // Templates
  @ViewChild('addressTpl', { static: true }) addressTpl!: TemplateRef<any>;
  @ViewChild('postalCodeTpl', { static: true })
  postalCodeTpl!: TemplateRef<any>;
  @ViewChild('cityTpl', { static: true }) cityTpl!: TemplateRef<any>;
  @ViewChild('countryTpl', { static: true }) countryTpl!: TemplateRef<any>;
  @ViewChild('roofTypeTpl', { static: true }) roofTypeTpl!: TemplateRef<any>;
  @ViewChild('buildingTypeTpl', { static: true })
  buildingTypeTpl!: TemplateRef<any>;

  columns: ColumnDef<Address>[] = [];

  readonly actions = computed<TableAction<Address>[]>(() => {
    const selectedCount = this.selectedAddressObjects().length;
    return [
      {
        label: 'Bearbeiten',
        icon: 'lucidePencil',
        action: (address) => this.onAction('edit', address),
        visible: () => this.currentOrgStore.can('address', 'update'),
      },
      {
        label: (_addr: Address) =>
          selectedCount > 1
            ? `Kampagne erstellen (${selectedCount})`
            : 'Kampagne erstellen',
        icon: 'lucideRocket',
        visible: () => this.currentOrgStore.can('organization', 'createcampaign'),
        dividerBefore: true,
        action: (address) => {
          const selected = this.selectedAddressObjects();
          this.createCampaignRequested.emit(selected.length > 0 ? selected : [address]);
        },
      },
      {
        label: 'Löschen',
        icon: 'lucideTrash',
        type: 'destructive' as const,
        action: (address) => this.onAction('delete', address),
        visible: () => this.currentOrgStore.can('address', 'delete'),
        dividerBefore: true,
      },
    ];
  });

  ngOnInit() {
    this.columns = [
      {
        header: 'Straße & Hausnummer',
        sortKey: 'address',
        sortValue: (row) => `${row.street ?? ''} ${row.houseNumber ?? ''}`,
        template: this.addressTpl,
      },
      {
        header: 'PLZ',
        sortKey: 'postalCode',
        sortValue: (row) => row.postalCode,
        template: this.postalCodeTpl,
      },
      {
        header: 'Stadt',
        sortKey: 'city',
        sortValue: (row) => row.city,
        template: this.cityTpl,
      },
      {
        header: 'Dachtyp',
        sortKey: 'roofType',
        sortValue: (row) => this.getRoofTypeLabel(row.roofType),
        template: this.roofTypeTpl,
      },
      {
        header: 'Gebäudetyp',
        sortKey: 'isApartmentBuilding',
        sortValue: (row) => row.isApartmentBuilding ? 'Mehrfamilienhaus' : 'Einfamilienhaus',
        template: this.buildingTypeTpl,
      },
    ];
  }

  onSelectionIdsChanged(ids: (string | number)[]): void {
    this.selectionChanged.emit(ids.map(String));
  }

  /** Called by the parent when "select all across all pages" is requested. */
  selectAllAddresses(allAddresses: Address[]): void {
    // Feed all rows into DataTable's knownRowsMap first so selectedObjects resolves immediately,
    // then set the IDs (which also emits selectionIdsChanged → onSelectionIdsChanged → parent).
    this.dataTable()?.feedKnownRows(allAddresses);
    this.dataTable()?.setSelectedIds(allAddresses.map((a) => a.addressId));
  }

  onAddNew(): void {
    this.editingAddress.set(null);
    this.isFormOpen.set(true);
  }

  onAction(type: AddressAction['type'], address: Address, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    // Handle edit action internally
    if (type === 'edit') {
      this.editingAddress.set(address);
      this.isFormOpen.set(true);
      return;
    }

    // Handle delete action internally
    if (type === 'delete') {
      this.handleDelete(address);
      return;
    }

    this.actionTriggered.emit({ type, address });
  }

  onFormClose(): void {
    this.isFormOpen.set(false);
    this.editingAddress.set(null);
  }

  onAddressSaved(address: Address): void {
    this.isFormOpen.set(false);

    if (this.editingAddress()) {
      this.addressUpdated.emit(address);
    } else {
      this.addressCreated.emit(address);
    }

    this.editingAddress.set(null);
  }

  private async handleDelete(address: Address): Promise<void> {
    const confirmed = await this.confirmationDialog.confirm({
      title: 'Adresse löschen',
      description: 'Sind Sie sicher, dass Sie diese Adresse löschen möchten?',
      type: 'danger',
      itemInfo: {
        label: this.formatFullAddress(address),
        sublabel: 'Adresse',
      },
      warningMessage:
        'Diese Aktion kann nicht rückgängig gemacht werden. Prüfen Sie vorher verknüpfte Analysen und Kampagnen.',
      confirmText: 'Adresse löschen',
    });

    if (!confirmed) return;

    try {
      await this.addressService.deleteAddress(
        this.organizationId(),
        address.addressId
      );
      this.toastService.success('Adresse wurde gelöscht');
      this.dataChanged.emit(); // Notify parent to reload data
    } catch (error) {
      console.error('[AddressTable] Error deleting address:', error);
      // Extract the backend error message from the error response
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unbekannter Fehler beim Löschen der Adresse';
      this.toastService.error('Fehler beim Löschen der Adresse', errorMessage);
    }
  }

  getRoofTypeBadgeColor(roofType: string | null | undefined): string {
    switch (roofType) {
      case 'flat':
        return 'bg-accent text-accent-foreground border-primary/20';
      case 'normal':
        return 'bg-success-soft text-success border-success';
      case 'both':
        return 'bg-secondary text-muted-foreground border-border';
      default:
        return 'bg-muted text-foreground border-border';
    }
  }

  getRoofTypeLabel(roofType: string | null | undefined): string {
    switch (roofType) {
      case 'flat':
        return 'Flachdach';
      case 'normal':
        return 'Normaldach';
      case 'both':
        return 'Beide';
      default:
        return 'Unbekannt';
    }
  }

  formatFullAddress(address: Address): string {
    return `${address.street} ${address.houseNumber}, ${address.postalCode} ${address.city}`;
  }
}
